import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { TabProps } from './Tab';
import { filterId } from '../../hooks/useFilterId';

interface TabsChildren extends React.ReactElement<TabProps> {}

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: 'alt' | 'default';
  activeTab?: number;
  listClassName?: string;
  children: TabsChildren | TabsChildren[] | undefined;
  onChangeTab?: (tab: TabsChildren) => void;
  preventChangingTab?: boolean;
}

const Tabs: React.FC<TabsProps> = (props) => {
  const {
    type = 'default',
    activeTab: userActiveTabIndex = 0,
    className,
    listClassName,
    children,
    onChangeTab,
    preventChangingTab = false,
    ...otherProps
  } = props;

  let childrenArr: TabsChildren[] = [];

  if (children !== undefined) {
    childrenArr = Array.isArray(children)
      ? (children as TabsChildren[])
      : [children];
  }

  const tabsClassName = classNames(className, 'ui-tabs', `ui-tabs--${type}`);
  const completeListClassName = classNames(listClassName, 'ui-tabs-list');

  const [activeTab, setActiveTab] = useState(userActiveTabIndex);

  useEffect(() => {
    setActiveTab(userActiveTabIndex);
  }, [userActiveTabIndex]);

  const handleTab = useCallback(
    (tabIndex: number, tabChild: TabsChildren) => {
      if (!preventChangingTab) {
        setActiveTab(tabIndex);
      }
      onChangeTab && onChangeTab(tabChild);
    },
    [onChangeTab, preventChangingTab]
  );

  let activeChild: TabsChildren | undefined;

  return (
    <div className={tabsClassName} {...otherProps}>
      <div role="tablist" className={completeListClassName}>
        {childrenArr.map((child, index) => {
          const { label } = child.props;

          const isActive = activeTab === index;

          const { className: tabClassName, onClick: tabOnClick, ...tabProps } =
            child.props.tabProps || {};

          const completeTabClassName = classNames(
            tabClassName,
            'ui-tabs-list-item',
            {
              'ui-tabs-list-item--active': isActive,
            }
          );

          if (isActive) {
            activeChild = child;
          }

          const id = filterId(label);

          return (
            <button
              type="button"
              id={`tab-${id}`}
              role="tab"
              aria-selected={isActive}
              key={id}
              className={completeTabClassName}
              aria-controls={`panel-${id}`}
              onClick={(event) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                tabOnClick && tabOnClick(event);

                if (!event.defaultPrevented) {
                  handleTab(index, child);
                }
              }}
              {...tabProps}
            >
              {label}
            </button>
          );
        })}
      </div>
      {activeChild}
    </div>
  );
};

export default Tabs;
